import { useApolloClient } from '@apollo/client/react';
import { Auth } from 'aws-amplify';
import DOMPurifyFactory from 'dompurify';
import { animate, motion, useMotionValue } from 'framer-motion';
import { marked } from 'marked';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation, useNavigate } from 'react-router-dom';

import ExpandIcon from '~/assets/ExpandIcon';
import PenIcon from '~/assets/PenIcon';
import PlusIcon from '~/assets/PlusIcon';
import {
  computeCurrentLocationDeeplink,
  createAssistantMessage,
  createErrorMessage,
  createUserMessage,
  mapApiMessageToUIMessage,
  MessagePart,
  MESSAGES_PER_PAGE,
  removeLastErrorMessage,
  SCROLL_THRESHOLD,
  UIMessage,
  USAGE_QUERY_DELAY,
} from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { TriggerPayload, useBedrockStream } from '~/components/AIInsightsModal/useBedrockStream';
import { useTypewriter } from '~/components/AIInsightsModal/useTypewriter';
import { Block } from '~/components/AIInsightsUI/blockTypes';
import { UIBlock } from '~/components/AIInsightsUI/UIBlock';
import ActionModal from '~/components/Modal/ActionModal/ActionModal';
import { conversationsPath } from '~/constants';
import { visitIQAiInsightsSidebarWidth } from '~/constants/localStorage';
import { useWindowDimensions } from '~/hooks/useDimensions';
import {
  ConversationRole,
  GetConversationsDocument,
  GetConversationsQuery,
  useConversationMessagesQuery,
  useCreateConversationMutation,
  useGetConversationsQuery,
  useTouchConversationAfterUserMessageMutation,
} from '~/types/api.graphql';
import { markExpandAnimation, useConversationsNav } from '~/views/ConversationsView/useConversationsNav';

import {
  ChatContainer,
  CloseButton,
  ConversationCountBadge,
  ConversationsDropdownWrapper,
  ConversationsPopoverFooter,
  ConversationsPopoverGradient,
  ConversationsPopoverItem,
  ConversationsPopoverItemTitle,
  ConversationsPopoverList,
  ConversationsPopoverListWrapper,
  ConversationsPopoverPanel,
  EditTitleButton,
  EmptyStateContainer,
  EmptyStateSubtitle,
  EmptyStateTitle,
  ExpandButton,
  ExpandedContentCenter,
  ExpandedEmptyContentBlock,
  LoadingContainer,
  LoadingDot,
  LoadingDotsContainer,
  LoadingMoreContainer,
  LoadingSubtitle,
  LoadingTitle,
  MarkdownContent,
  MessageBubble,
  MessageContainer,
  MessagesList,
  MessagesListWrapper,
  MessagesScrollGradient,
  MobileBottomSheetContent,
  NewConversationIconButton,
  ResizeHandle,
  SidebarHeader,
  SidebarInner,
  SidebarTitleContainer,
  SidebarTitleText,
  SidebarWrapper,
  StyledBottomSheet,
  SuggestedActionsContainer,
  ToolStatusContainer,
  ToolStatusDot,
  ToolStatusText,
  UserMessageBubble,
} from './AIInsightsSidebar.styles';
import {
  CONVERSATION_MAX_TITLE_LENGTH,
  ConversationListItem,
  CONVERSATIONS_PER_PAGE,
  wrapTablesForScroll,
} from './AIInsightsSidebar.utils';
import { MIN_SIDEBAR_WIDTH, useAIInsightsSidebar } from './AIInsightsSidebarContext';
import AIInsightsSidebarExpandedHeader from './AIInsightsSidebarExpandedHeader';
import AIInsightsSidebarInput, { AIInsightsSidebarInputHandle } from './AIInsightsSidebarInput';
import { renderBodyWithChipLinks, stripMentionMarkdown } from './mentionChipUtils';
import { useSessionLimitBanner } from './SessionLimitBanner/useSessionLimitBanner';
import SuggestedActionItem from './SuggestedActionItem';
import UpdateConversationNameView, {
  UpdateConversationNameViewProps,
} from './UpdateConversationNameView/UpdateConversationNameView';
import { useToolStatus } from './useToolStatus';

// Explicitly create instance with current window to avoid issues when the
// module-level `createDOMPurify()` runs before DOM is fully ready
const DOMPurify = DOMPurifyFactory(window);

// Wraps SidebarWrapper with framer-motion for animated width transitions.
// Defined at module level so its type is stable and avoids remounts.
const MotionSidebarWrapper = motion(SidebarWrapper);

// Width transition for the expand/collapse animation. easeOut so movement
// starts immediately and decelerates into place (no slow start, no bounce).
const EASE_TRANSITION = { duration: 0.3, ease: 'easeOut' } as const;

// DOMPurify options shared by all message bubbles that render mention chips.
// The extra tags/attrs allow SVG icons inside chips to survive sanitisation.
const CHIP_DOMPURIFY_OPTIONS = {
  ADD_TAGS: ['svg', 'path', 'circle'],
  ADD_ATTR: [
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-dasharray',
    'fill-rule',
    'viewBox',
    'xmlns',
    'aria-hidden',
  ],
};

const AIInsightsSidebar = () => {
  const { t } = useTranslation();
  const { isInMobile } = useWindowDimensions();
  // Context is always present here since AIInsightsSidebar is rendered inside the provider
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ctx = useAIInsightsSidebar()!;
  const {
    isOpen,
    isExpanded,
    expandedSlotRect,
    section,
    entityDeepLink,
    conversationId,
    closeSidebar,
    sidebarWidth,
    maxSidebarWidth,
    setSidebarWidth,
    setIsResizing,
    isResizing,
    setConversationId,
    clickCounterCTA,
    skipExpandAnimation,
  } = ctx;

  const location = useLocation();
  const navigate = useNavigate();
  const { navigateAfterFirstMessage, collapseAndReturn } = useConversationsNav();

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const inputRef = useRef<AIInsightsSidebarInputHandle>(null);
  const expandedInputRef = useRef<HTMLDivElement>(null);
  const [expandedInputHeight, setExpandedInputHeight] = useState(0);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showPopoverTopGradient, setShowPopoverTopGradient] = useState(false);
  const [showPopoverBottomGradient, setShowPopoverBottomGradient] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, right: 0 });
  const [showMessagesTopGradient, setShowMessagesTopGradient] = useState(false);
  const [showMessagesBottomGradient, setShowMessagesBottomGradient] = useState(false);

  // ---------------------------------------------------------------------------
  // Open animation state
  // ---------------------------------------------------------------------------
  const [openAnimKey, setOpenAnimKey] = useState(0);
  const [inputInternalsVisible, setInputInternalsVisible] = useState(true);

  // ---------------------------------------------------------------------------
  // Resize handler
  // ---------------------------------------------------------------------------
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX: number = e.clientX;
      const startWidth: number = sidebarWidth;
      setIsResizing(true);

      const onMouseMove = (ev: MouseEvent) => {
        const delta: number = startX - ev.clientX; // drag left → wider
        setSidebarWidth(Math.max(MIN_SIDEBAR_WIDTH, Math.min(maxSidebarWidth, startWidth + delta)));
      };

      const onMouseUp = (ev: MouseEvent) => {
        const finalWidth: number = Math.max(
          MIN_SIDEBAR_WIDTH,
          Math.min(maxSidebarWidth, startWidth + (startX - ev.clientX)),
        );
        localStorage.setItem(visitIQAiInsightsSidebarWidth, `${finalWidth}`);
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [sidebarWidth, maxSidebarWidth, setSidebarWidth, setIsResizing],
  );

  // ---------------------------------------------------------------------------
  // Chat state
  // ---------------------------------------------------------------------------
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [streamingParts, setStreamingParts] = useState<MessagePart[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------
  const conversationsDropdownRef = useOnclickOutside(() => setIsPopoverOpen(false));
  const updateConversationModalRef = useRef<{ open: () => void } | null>(null);
  const popoverListRef = useRef<HTMLDivElement>(null);
  const conversationBadgeRef = useRef<HTMLButtonElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const streamingBufferRef = useRef<string>('');
  const displayedLengthRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);
  const isLocalSessionRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const scrollHeightBeforeLoadRef = useRef(0);
  const isSidebarOpenRef = useRef(false);
  const userScrolledUpRef = useRef(false);
  const shouldAnimateRef = useRef(false);
  const prevConversationIdRef = useRef(conversationId);

  const [hasReceivedText, setHasReceivedText] = useState(false);

  // Session-limit usage query, dismissal state, banner + info-modal nodes.
  const { isSessionLimitReached, refetchCreditsUsage, bannerNode, infoModalNode } = useSessionLimitBanner(isOpen);

  // ---------------------------------------------------------------------------
  // Queries & mutations
  // ---------------------------------------------------------------------------
  const {
    data: conversationsData,
    loading: isLoadingConversations,
    fetchMore: fetchMoreConversations,
  } = useGetConversationsQuery({
    variables: { first: CONVERSATIONS_PER_PAGE },
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data,
    loading: isLoadingMessages,
    fetchMore: fetchMoreMessages,
  } = useConversationMessagesQuery({
    variables: {
      conversationId,
      first: MESSAGES_PER_PAGE,
    },
    skip: !isOpen || !conversationId,
    fetchPolicy: 'network-only',
  });

  const apolloClient = useApolloClient();
  const [createConversationMutation] = useCreateConversationMutation();
  const [touchConversationMutation] = useTouchConversationAfterUserMessageMutation();

  const moveConversationToTop = useCallback(
    (convId: string) => {
      const cached = apolloClient.readQuery<GetConversationsQuery>({
        query: GetConversationsDocument,
        variables: { first: CONVERSATIONS_PER_PAGE },
      });
      if (!cached) return;
      const nodes = cached.conversations.nodes;
      const idx = nodes.findIndex((c) => c.id === convId);
      if (idx <= 0) return; // already first or not found
      apolloClient.writeQuery<GetConversationsQuery>({
        query: GetConversationsDocument,
        variables: { first: CONVERSATIONS_PER_PAGE },
        data: {
          ...cached,
          conversations: {
            ...cached.conversations,
            nodes: [nodes[idx], ...nodes.slice(0, idx), ...nodes.slice(idx + 1)],
          },
        },
      });
    },
    [apolloClient],
  );

  const hasMoreMessages = data?.conversationMessages?.pageInfo?.hasNextPage ?? false;
  const messagesEndCursor = data?.conversationMessages?.pageInfo?.endCursor;
  const agentSessionId = data?.conversationMessages?.agentSessionId;
  const messagesConversationTitle = data?.conversationMessages?.title ?? null;

  const totalConversationCount = conversationsData?.conversations?.totalCount ?? 0;
  const hasMoreConversations = conversationsData?.conversations?.pageInfo?.hasNextPage ?? false;
  const conversationsEndCursor = conversationsData?.conversations?.pageInfo?.endCursor;

  // ---------------------------------------------------------------------------
  // Derived conversation title (shown in header)
  // ---------------------------------------------------------------------------
  // Priority: preloaded list title → messages query title → fallback.
  // The list title is an instant-from-cache optimisation; messagesConversationTitle covers
  // conversations not in the preloaded list (old convos via search, page refresh).
  const currentConversationTitle = useMemo(() => {
    const listTitle = conversationsData?.conversations?.nodes?.find((c) => c.id === conversationId)?.title;
    return listTitle ?? messagesConversationTitle ?? (conversationId ? '' : t('aiInsights.newConversation'));
  }, [conversationId, conversationsData, messagesConversationTitle, t]);

  // ---------------------------------------------------------------------------
  // Scroll helpers
  // ---------------------------------------------------------------------------
  const scrollToBottom = useCallback((force = false) => {
    if (messagesListRef.current && (force || !userScrolledUpRef.current)) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, []);

  const updateMessagesGradients = useCallback(() => {
    const el = messagesListRef.current;
    if (!el) return;
    setShowMessagesTopGradient(el.scrollTop > 4);
    setShowMessagesBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.deltaY < 0) {
      userScrolledUpRef.current = true;
    } else if (e.deltaY > 0 && messagesListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        userScrolledUpRef.current = false;
      }
    }
  }, []);

  const maintainScrollPosition = useCallback((previousScrollHeight: number) => {
    setTimeout(() => {
      if (messagesListRef.current) {
        const newScrollHeight = messagesListRef.current.scrollHeight;
        messagesListRef.current.scrollTop = newScrollHeight - previousScrollHeight;
      }
      isLoadingMoreRef.current = false;
    }, 0);
  }, []);

  // ---------------------------------------------------------------------------
  // Tool status helpers
  // ---------------------------------------------------------------------------
  const { toolStatus, toolStatusVisible, handleToolStatus: handleToolStatusBase, resetToolStatus } = useToolStatus();

  const handleToolStatus = useCallback(
    (status: string | null) => {
      if (status === null) {
        shouldAnimateRef.current = true;
        // flushSync forces a synchronous re-render so the element becomes visible right away
        // instead of waiting for React to flush the batched updates after the stream ends
        flushSync(() => {
          setHasReceivedText(true);
        });
      }
      handleToolStatusBase(status);
    },
    [handleToolStatusBase],
  );

  const streamingRefCallback = useCallback((node: HTMLDivElement | null) => {
    streamingMessageRef.current = node;
    if (node && streamingBufferRef.current.length > 0) {
      node.innerHTML = wrapTablesForScroll(DOMPurify.sanitize(marked.parse(streamingBufferRef.current) as string));
      displayedLengthRef.current = streamingBufferRef.current.length;
    }
  }, []);

  const handleBlock = useCallback((block: Block, precedingText: string) => {
    // Close the completed trailing text run into a text part, then add the block part.
    // precedingText comes from the stream (the buffer is already reset by the time this fires).
    // Reset the typewriter node so post-chart text streams fresh into it.
    setStreamingParts((prev) => [
      ...prev,
      ...(precedingText ? [{ type: 'text' as const, text: precedingText }] : []),
      { type: 'block' as const, block },
    ]);
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    if (streamingMessageRef.current) {
      streamingMessageRef.current.innerHTML = '';
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  const { startTypewriter, stopTypewriter, waitForTypewriterCompletion } = useTypewriter({
    streamingBufferRef,
    displayedLengthRef,
    streamingMessageRef,
    scrollToBottom,
    shouldAnimateRef,
  });

  const currentLocationDeeplink = computeCurrentLocationDeeplink(entityDeepLink, location.pathname);

  const { sendMessage: sendBedrockMessage } = useBedrockStream({
    sessionIdRef,
    abortControllerRef,
    streamingBufferRef,
    currentLocationDeeplink,
    onToolStatus: handleToolStatus,
    onBlock: handleBlock,
  });

  // ---------------------------------------------------------------------------
  // Reset helpers
  // ---------------------------------------------------------------------------
  const resetChatState = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    stopTypewriter();
    resetToolStatus();
    setIsSending(false);
    setIsStreaming(false);
    setHasReceivedText(false);
    shouldAnimateRef.current = false;
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    setMessages([]);
    setSuggestedActions([]);
    setStreamingParts([]);
    sessionIdRef.current = null;
    isInitialLoadRef.current = true;
    isLocalSessionRef.current = false;
    userScrolledUpRef.current = false;
  }, [stopTypewriter, resetToolStatus]);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------
  const handleSelectConversation = useCallback(
    (conv: ConversationListItem) => {
      // If already on this conversation just close the popover — don't reset
      if (conv.id === conversationId) {
        setIsPopoverOpen(false);
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      // No reset of input — draft is preserved when switching conversations
      resetChatState();
      setConversationId(conv.id);
      setIsPopoverOpen(false);
    },
    [conversationId, resetChatState, setConversationId],
  );

  const handleNewConversation = useCallback(() => {
    resetChatState();
    inputRef.current?.reset();
    setConversationId(null);
    setIsPopoverOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [resetChatState, setConversationId]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Reset when sidebar closes; kick off opening animations when it opens
  useEffect(() => {
    if (isOpen) {
      isSidebarOpenRef.current = true;
      setOpenAnimKey((k) => k + 1);
      if (skipExpandAnimation) {
        setInputInternalsVisible(true);
        return undefined;
      }
      setInputInternalsVisible(false);

      const id = setTimeout(() => {
        setInputInternalsVisible(true);
      }, 200);

      return () => clearTimeout(id);
    }

    isSidebarOpenRef.current = false;
    resetChatState();
    inputRef.current?.reset();
    setIsPopoverOpen(false);
    setInputInternalsVisible(true);
    return undefined;
  }, [isOpen, resetChatState, skipExpandAnimation]);

  // Reset chat state when conversationId changes via external navigation (e.g. route/URL),
  // which bypasses handleSelectConversation. Also covers navigating back to the index
  // (conversationId → null) while expanded.
  useEffect(() => {
    if (prevConversationIdRef.current === conversationId) return;
    prevConversationIdRef.current = conversationId;
    resetChatState();
  }, [conversationId, resetChatState]);

  // Load messages into state when query data arrives
  useEffect(() => {
    if (!isOpen || !data?.conversationMessages?.nodes) return;
    // Skip when the conversation was created locally this session — messages are managed by the stream
    if (isLocalSessionRef.current) return;

    const apiMessages = data.conversationMessages.nodes.map(mapApiMessageToUIMessage);
    const chronologicalMessages = [...apiMessages].reverse();
    const wasLoadingMore = isLoadingMoreRef.current;
    const previousScrollHeight = scrollHeightBeforeLoadRef.current;

    setMessages(chronologicalMessages);

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setTimeout(() => {
        scrollToBottom(true);
        updateMessagesGradients();
      }, 50);
    } else if (wasLoadingMore) {
      maintainScrollPosition(previousScrollHeight);
    }
    setTimeout(updateMessagesGradients, 0);
  }, [isOpen, data, scrollToBottom, maintainScrollPosition, updateMessagesGradients]);

  // Sync agentSessionId from query result into ref
  useEffect(() => {
    if (agentSessionId) {
      sessionIdRef.current = agentSessionId;
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [agentSessionId]);

  // MotionValue is the single source of truth for sidebar width. Driving it
  // imperatively (instead of via the `animate` prop) avoids framer-motion's
  // dual-source conflict — width never animates on direct page load.
  const sidebarWidthMV = useMotionValue(0);
  useLayoutEffect(() => {
    const expandedW = expandedSlotRect?.width ?? sidebarWidth;
    const w = isExpanded ? expandedW : isOpen ? sidebarWidth : 0;
    // Instant on direct load (skip) and live drag-resize. Never animate width here.
    if (skipExpandAnimation || isResizing) {
      sidebarWidthMV.set(w);
      return undefined;
    }
    const controls = animate(sidebarWidthMV, w, EASE_TRANSITION);
    return () => controls.stop();
  }, [skipExpandAnimation, isExpanded, isOpen, expandedSlotRect, sidebarWidth, isResizing, sidebarWidthMV]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreMessages || isLoadingMessages || isLoadingMore || !messagesEndCursor) return;

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    scrollHeightBeforeLoadRef.current = messagesListRef.current?.scrollHeight ?? 0;

    try {
      await fetchMoreMessages({
        variables: { after: messagesEndCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.conversationMessages) return prev;
          return {
            ...prev,
            conversationMessages: {
              ...fetchMoreResult.conversationMessages,
              // preserve agentSessionId — fetchMore pages return null for it
              agentSessionId: prev.conversationMessages.agentSessionId,
              nodes: [
                ...(prev.conversationMessages.nodes ?? []),
                ...(fetchMoreResult.conversationMessages.nodes ?? []),
              ],
            },
          };
        },
      });
    } catch {
      isLoadingMoreRef.current = false;
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreMessages, isLoadingMessages, isLoadingMore, messagesEndCursor, fetchMoreMessages]);

  const handleScroll = useCallback(() => {
    if (!messagesListRef.current || isLoadingMore) return;
    if (messagesListRef.current.scrollTop < SCROLL_THRESHOLD && hasMoreMessages) {
      void handleLoadMore();
    }
    updateMessagesGradients();
  }, [hasMoreMessages, handleLoadMore, isLoadingMore, updateMessagesGradients]);

  const updatePopoverGradients = useCallback(() => {
    const el = popoverListRef.current;
    if (!el) return;
    setShowPopoverTopGradient(el.scrollTop > 0);
    setShowPopoverBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  const loadMoreConversations = useCallback(() => {
    if (!conversationsEndCursor) return;
    void fetchMoreConversations({
      variables: {
        first: CONVERSATIONS_PER_PAGE,
        after: conversationsEndCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.conversations) return prev;
        return {
          ...prev,
          conversations: {
            ...fetchMoreResult.conversations,
            nodes: [...prev.conversations.nodes, ...fetchMoreResult.conversations.nodes],
          },
        };
      },
    });
  }, [conversationsEndCursor, fetchMoreConversations]);

  // Recompute gradients when the popover opens or its data changes
  useEffect(() => {
    if (!isPopoverOpen) return;
    // Use rAF to let the DOM paint first
    requestAnimationFrame(updatePopoverGradients);
  }, [isPopoverOpen, conversationsData, updatePopoverGradients]);

  const startStream = useCallback(
    async (payload: TriggerPayload, displayText?: string) => {
      if (isSending || isStreaming || isSessionLimitReached) return;

      let shouldHitTouchMutation = true;

      // For new conversations (no conversationId yet): create the backend record on first send.
      // Title is seeded from the first user message excerpt.
      // Session ID = `${userId}-${conversationId}` per server-side getSessionId().
      let activeConversationId = conversationId;
      if (!conversationId && !sessionIdRef.current) {
        try {
          shouldHitTouchMutation = false;

          // Get session first — if it fails we haven't created anything yet
          const session = await Auth.currentSession();
          const trimmed = displayText?.trim();
          const titleText = trimmed ? stripMentionMarkdown(trimmed) : undefined;
          const excerpt = titleText
            ? titleText.slice(0, CONVERSATION_MAX_TITLE_LENGTH).trimEnd()
            : t('aiInsights.newConversation');
          const title = titleText && titleText.length > CONVERSATION_MAX_TITLE_LENGTH ? `${excerpt}...` : excerpt;
          const result = await createConversationMutation({
            variables: { title },
          });
          const newConv = result.data?.createConversation;
          if (!newConv) throw new Error('Failed to create conversation');
          // decodePayload() returns an any-typed map; cast explicitly to satisfy lint
          const userId = String(session.getIdToken().decodePayload().sub as unknown);
          sessionIdRef.current = userId.concat('-', newConv.id);
          isLocalSessionRef.current = true;
          setConversationId(newConv.id);
          // Pre-seed the guard so the [conversationId] reset effect treats this
          // as already-known and does not abort the in-flight send.
          prevConversationIdRef.current = newConv.id;
          activeConversationId = newConv.id;
          if (isExpanded && location.pathname === conversationsPath) {
            navigateAfterFirstMessage(newConv.id);
          }
          // Prepend new conversation to cache so it appears in the list immediately
          const cached = apolloClient.readQuery<GetConversationsQuery>({
            query: GetConversationsDocument,
            variables: { first: CONVERSATIONS_PER_PAGE },
          });
          if (cached) {
            apolloClient.writeQuery<GetConversationsQuery>({
              query: GetConversationsDocument,
              variables: { first: CONVERSATIONS_PER_PAGE },
              data: {
                ...cached,
                conversations: {
                  ...cached.conversations,
                  nodes: [newConv, ...cached.conversations.nodes],
                  totalCount: cached.conversations.totalCount + 1,
                },
              },
            });
          }
        } catch {
          setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.errorSendingMessage'))]);
          return;
        }
      }

      if (!sessionIdRef.current) return;

      setIsSending(true);
      setIsStreaming(true);
      setSuggestedActions([]);
      setHasReceivedText(false);
      shouldAnimateRef.current = false;
      streamingBufferRef.current = '';
      displayedLengthRef.current = 0;
      userScrolledUpRef.current = false;
      startTypewriter();

      if (displayText) {
        setMessages((prev) => [...removeLastErrorMessage(prev), createUserMessage(displayText)]);
      }
      setTimeout(() => scrollToBottom(true), 0);

      abortControllerRef.current = new AbortController();

      try {
        setIsSending(false);

        if (activeConversationId) {
          moveConversationToTop(activeConversationId);
          if (shouldHitTouchMutation) {
            void touchConversationMutation({
              variables: { conversationId: activeConversationId },
            });
          }
        }

        const result = await sendBedrockMessage(payload);

        if (streamingBufferRef.current.length > 0) {
          await waitForTypewriterCompletion();
        }

        if (result.parts.length > 0) {
          setMessages((prev) => [...prev, createAssistantMessage(result.parts)]);
        }
        setStreamingParts([]);
        if (result.actions.length > 0) {
          setSuggestedActions(result.actions);
          requestAnimationFrame(() => {
            setTimeout(() => scrollToBottom(true), 100);
          });
        }
      } catch (error: unknown) {
        if ((error as Error).name === 'AbortError') return;
        setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.errorSendingMessage'))]);
      } finally {
        stopTypewriter();
        resetToolStatus();
        setIsSending(false);
        setIsStreaming(false);
        setHasReceivedText(false);
        shouldAnimateRef.current = false;
        streamingBufferRef.current = '';
        displayedLengthRef.current = 0;
        abortControllerRef.current = null;
        setStreamingParts([]);
        // Refresh usage after a short delay so the BE has time to persist the
        // new usage before we query (runs on success, error, or abort).
        setTimeout(() => void refetchCreditsUsage(), USAGE_QUERY_DELAY);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [
      isSending,
      isStreaming,
      isSessionLimitReached,
      conversationId,
      createConversationMutation,
      touchConversationMutation,
      moveConversationToTop,
      t,
      setConversationId,
      startTypewriter,
      stopTypewriter,
      sendBedrockMessage,
      waitForTypewriterCompletion,
      scrollToBottom,
      resetToolStatus,
      refetchCreditsUsage,
      isExpanded,
      location.pathname,
      navigateAfterFirstMessage,
    ],
  );

  const handleActionClick = useCallback(
    async (action: string) => {
      await startStream({ prompt: action }, action);
    },
    [startStream],
  );

  // Stable submit callback passed to AIInsightsSidebarInput — identity must not
  // change on navigation or streaming so the memoized child doesn't re-render.
  // startStream's useCallback deps include conversationId, so referencing it
  // directly would give onSubmit a fresh identity on every navigation and defeat
  // the memoized input's bail-out. Route through a live ref instead.
  const startStreamRef = useRef(startStream);
  startStreamRef.current = startStream;
  const handleSubmit = useCallback((prompt: string) => {
    void startStreamRef.current({ prompt }, prompt);
  }, []);

  const handleClose = useCallback(() => {
    if (isExpanded) {
      collapseAndReturn();
    } else {
      closeSidebar();
    }
  }, [isExpanded, collapseAndReturn, closeSidebar]);

  const expandNavTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (expandNavTimeoutRef.current) clearTimeout(expandNavTimeoutRef.current);
    },
    [],
  );

  const handleExpand = useCallback(() => {
    const { setIsExpandFading } = ctx;
    setIsExpandFading(true);
    markExpandAnimation();
    const targetPath = conversationId ? `${conversationsPath}/${conversationId}` : conversationsPath;
    expandNavTimeoutRef.current = setTimeout(() => {
      navigate(targetPath, {
        state: { origin: location.pathname + location.search },
      });
    }, 200);
  }, [ctx, conversationId, navigate, location.pathname, location.search]);

  // Intercept clicks on mention chip links inside message bubbles so
  // navigation is handled by React Router (SPA, no full-page reload).
  const handleMessageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const chip = (e.target as HTMLElement).closest<HTMLAnchorElement>('a.mention-chip');
      if (!chip) return;
      e.preventDefault();
      const appPath = chip.getAttribute('href');
      if (appPath) navigate(appPath);
    },
    [navigate],
  );

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  // Loading only applies to existing conversations (new conversations start empty immediately)
  const isInitialLoading = !!conversationId && !isLoadingMore && (isLoadingMessages || !agentSessionId);
  const isEmpty = !isInitialLoading && messages.length === 0 && !isStreaming;

  // Track expanded input height so MessagesList can pad its bottom accordingly.
  // Depends on isEmpty: the input element (expandedInputRef) only exists in the
  // non-empty branch, so it must be re-measured when the layout swaps (e.g.
  // navigating from an empty conversation to one with messages), otherwise
  // padding stays 0 and the last messages hide behind the absolute input.
  useLayoutEffect(() => {
    const el = expandedInputRef.current;
    if (!isExpanded || !el) {
      setExpandedInputHeight(0);
      return undefined;
    }

    setExpandedInputHeight(el.offsetHeight);
    const observer = new ResizeObserver(() => {
      setExpandedInputHeight(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
    // expandedInputRef is stable; re-run when expand state or the empty/non-empty branch changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, isEmpty]);

  const renderLoadingState = () => (
    <LoadingContainer>
      <LoadingTitle>{t('aiInsights.loadingMessages')}</LoadingTitle>
      <LoadingSubtitle>{t('aiInsights.loadingMessagesSubtitle')}</LoadingSubtitle>
      <LoadingDotsContainer>
        {[...Array(6)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingContainer>
  );

  const renderEmptyState = (compact = false) => (
    <EmptyStateContainer $compact={compact}>
      <motion.div
        key={`empty-title-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.25, ease: 'easeOut' }}
      >
        <EmptyStateTitle>{t('aiInsights.emptyStateTitle')}</EmptyStateTitle>
      </motion.div>
      <motion.div
        key={`empty-subtitle-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.75, ease: 'easeOut' }}
      >
        <EmptyStateSubtitle>{t('aiInsights.emptyStateSubtitle')}</EmptyStateSubtitle>
      </motion.div>
    </EmptyStateContainer>
  );

  const renderLoadingMore = () => (
    <LoadingMoreContainer>
      <LoadingDotsContainer>
        {[...Array(3)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingMoreContainer>
  );

  const renderMessages = () => (
    <>
      {isLoadingMore && renderLoadingMore()}
      {messages.map((message: UIMessage) => (
        <React.Fragment key={message.id}>
          <MessageContainer $isUser={message.role === ConversationRole.User}>
            {message.role === ConversationRole.User ? (
              <UserMessageBubble>
                <MarkdownContent
                  dangerouslySetInnerHTML={{
                    __html: wrapTablesForScroll(
                      DOMPurify.sanitize(
                        marked.parse(renderBodyWithChipLinks(message.body ?? '')) as string,
                        CHIP_DOMPURIFY_OPTIONS,
                      ),
                    ),
                  }}
                />
              </UserMessageBubble>
            ) : message.parts?.length ? (
              // Render ordered parts (text/block/text/…) in arrival order.
              // Legacy messages without parts fall back to body below.
              <MessageBubble>
                {message.parts.map((part, i) =>
                  part.type === 'text' ? (
                    <MarkdownContent
                      key={i}
                      dangerouslySetInnerHTML={{
                        __html: wrapTablesForScroll(
                          DOMPurify.sanitize(
                            marked.parse(renderBodyWithChipLinks(part.text)) as string,
                            CHIP_DOMPURIFY_OPTIONS,
                          ),
                        ),
                      }}
                    />
                  ) : (
                    <UIBlock key={i} block={part.block} />
                  ),
                )}
              </MessageBubble>
            ) : (
              // Legacy fallback: message has no parts (e.g. older persisted messages)
              <MessageBubble>
                <MarkdownContent
                  dangerouslySetInnerHTML={{
                    __html: wrapTablesForScroll(
                      DOMPurify.sanitize(
                        marked.parse(renderBodyWithChipLinks(message.body ?? '')) as string,
                        CHIP_DOMPURIFY_OPTIONS,
                      ),
                    ),
                  }}
                />
              </MessageBubble>
            )}
          </MessageContainer>
        </React.Fragment>
      ))}
      {isStreaming && (
        <MessageContainer>
          <MessageBubble>
            {/* Render completed parts (text or chart) in arrival order */}
            {streamingParts.map((part, i) =>
              part.type === 'text' ? (
                <MarkdownContent
                  key={i}
                  dangerouslySetInnerHTML={{
                    __html: wrapTablesForScroll(
                      DOMPurify.sanitize(
                        marked.parse(renderBodyWithChipLinks(part.text)) as string,
                        CHIP_DOMPURIFY_OPTIONS,
                      ),
                    ),
                  }}
                />
              ) : (
                <UIBlock key={i} block={part.block} />
              ),
            )}
            {/* Typewriter node for the still-animating trailing text run.
                Always mounted so the typewriter can write to it; hidden until first text arrives. */}
            <MarkdownContent ref={streamingRefCallback} style={hasReceivedText ? undefined : { display: 'none' }} />
            {/* Thinking indicator: initial state before text, or mid-stream while agent is thinking.
                NOTE: a "chart loading" shimmer is not possible here because the block stream event only
                fires once the full chart JSON is ready — the agent/BE emits no pre-block loading signal.
                Implementing a per-chart shimmer requires the backend/agent to emit a loading marker first. */}
            {(!hasReceivedText || toolStatus) && (
              <ToolStatusContainer>
                <ToolStatusDot />
                <ToolStatusText $visible={!toolStatus || toolStatusVisible}>
                  {toolStatus ?? t('aiInsights.thinking')}
                </ToolStatusText>
              </ToolStatusContainer>
            )}
          </MessageBubble>
        </MessageContainer>
      )}
    </>
  );

  const shouldShowSuggestedActions = !isStreaming && suggestedActions.length > 0;

  const renderChatContent = () => {
    if (isInitialLoading) return renderLoadingState();
    if (isEmpty) return renderEmptyState();
    return renderMessages();
  };

  const renderConversationsPopover = () => {
    const convs = conversationsData?.conversations?.nodes ?? [];
    return (
      <ConversationsDropdownWrapper ref={conversationsDropdownRef}>
        <ConversationCountBadge
          ref={conversationBadgeRef}
          type="button"
          aria-label={t('aiInsights.conversations')}
          onClick={() => {
            if (!isPopoverOpen && conversationBadgeRef.current) {
              const rect = conversationBadgeRef.current.getBoundingClientRect();
              setPopoverPos({
                top: rect.bottom + 6,
                right: window.innerWidth - rect.right,
              });
            }
            setIsPopoverOpen((prev) => !prev);
          }}
        >
          {totalConversationCount}
        </ConversationCountBadge>
        <ConversationsPopoverPanel $isOpen={isPopoverOpen} $top={popoverPos.top} $right={popoverPos.right}>
          <ConversationsPopoverListWrapper>
            <ConversationsPopoverGradient $visible={showPopoverTopGradient} $position="top" />
            <ConversationsPopoverList
              id="conversationsPopoverList"
              ref={popoverListRef}
              onScroll={updatePopoverGradients}
            >
              {isLoadingConversations && convs.length === 0 ? (
                <ConversationsPopoverItemTitle as="div" style={{ padding: '0.625rem 0.875rem' }}>
                  {t('aiInsights.loading')}
                </ConversationsPopoverItemTitle>
              ) : convs.length === 0 ? (
                <ConversationsPopoverItemTitle as="div" style={{ padding: '0.625rem 0.875rem' }}>
                  {t('aiInsights.noConversationsYet')}
                </ConversationsPopoverItemTitle>
              ) : (
                <InfiniteScroll
                  dataLength={convs.length}
                  next={loadMoreConversations}
                  hasMore={hasMoreConversations}
                  loader={null}
                  scrollableTarget="conversationsPopoverList"
                  scrollThreshold="40px"
                  style={{ overflow: 'hidden' }}
                >
                  {convs.map((conv) => (
                    <ConversationsPopoverItem
                      key={conv.id}
                      type="button"
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <ConversationsPopoverItemTitle>{conv.title}</ConversationsPopoverItemTitle>
                    </ConversationsPopoverItem>
                  ))}
                </InfiniteScroll>
              )}
            </ConversationsPopoverList>
            <ConversationsPopoverGradient $visible={showPopoverBottomGradient} $position="bottom" />
          </ConversationsPopoverListWrapper>
          <ConversationsPopoverFooter>{t('aiInsights.conversationStorageDisclaimer')}</ConversationsPopoverFooter>
        </ConversationsPopoverPanel>
      </ConversationsDropdownWrapper>
    );
  };

  // ---------------------------------------------------------------------------
  // Input section — single stable element reused in all render branches.
  // All primitives / stable callbacks → memo bail-out on nav / stream re-renders.
  // ---------------------------------------------------------------------------
  // Expanded landing (no messages yet) centers the empty block + input as a group.
  const emptyExpanded = isExpanded && isEmpty;

  const suggestedActionsNode = shouldShowSuggestedActions ? (
    <SuggestedActionsContainer>
      {suggestedActions.map((action, index) => (
        <SuggestedActionItem key={`action-${index}`} label={action} onClick={() => void handleActionClick(action)} />
      ))}
    </SuggestedActionsContainer>
  ) : null;

  const inputSection = (
    <AIInsightsSidebarInput
      ref={inputRef}
      section={section}
      isOpen={isOpen}
      clickCounterCTA={clickCounterCTA}
      isExpanded={isExpanded}
      isInMobile={isInMobile}
      openAnimKey={openAnimKey}
      skipExpandAnimation={skipExpandAnimation}
      inputInternalsVisible={inputInternalsVisible}
      isSending={isSending}
      isStreaming={isStreaming}
      isSessionPending={!!conversationId && !agentSessionId}
      isSessionLimitReached={isSessionLimitReached}
      banner={bannerNode}
      onSubmit={handleSubmit}
    />
  );

  // ---------------------------------------------------------------------------
  // Full inner render
  // ---------------------------------------------------------------------------

  const renderInner = (fixedWidth = false) => (
    <SidebarInner $width={fixedWidth ? sidebarWidth : 0} $isExpanded={isExpanded}>
      <motion.div
        key={`header-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {isExpanded ? (
          <AIInsightsSidebarExpandedHeader
            title={currentConversationTitle}
            onCollapse={handleClose}
            onEditName={conversationId ? () => updateConversationModalRef.current?.open() : undefined}
          />
        ) : (
          <SidebarHeader>
            <NewConversationIconButton
              type="button"
              onClick={handleNewConversation}
              aria-label={t('aiInsights.newConversation')}
            >
              <PlusIcon />
            </NewConversationIconButton>
            <SidebarTitleContainer>
              <SidebarTitleText title={currentConversationTitle}>{currentConversationTitle}</SidebarTitleText>
              {conversationId && (
                <EditTitleButton
                  onClick={() => updateConversationModalRef.current?.open()}
                  aria-label={t('aiInsights.editConversationName')}
                >
                  <PenIcon />
                </EditTitleButton>
              )}
            </SidebarTitleContainer>
            {renderConversationsPopover()}
            {!isInMobile && (
              <ExpandButton onClick={handleExpand} aria-label={t('aiInsights.expand')}>
                <ExpandIcon />
              </ExpandButton>
            )}
            <CloseButton onClick={handleClose} aria-label={t('common.close')}>
              ✕
            </CloseButton>
          </SidebarHeader>
        )}
      </motion.div>
      <ChatContainer $isExpanded={isExpanded} $emptyExpanded={emptyExpanded}>
        {/* Content above the input swaps (centered empty block vs messages list);
            the input itself is rendered once below, in a single stable tree slot,
            so toggling isEmpty never remounts it. */}
        {emptyExpanded ? (
          <ExpandedEmptyContentBlock>
            {renderEmptyState(true)}
            {suggestedActionsNode}
          </ExpandedEmptyContentBlock>
        ) : (
          <MessagesListWrapper>
            <MessagesScrollGradient $position="top" $visible={!isExpanded && showMessagesTopGradient} />
            <MessagesList
              ref={messagesListRef}
              $isExpanded={isExpanded}
              onScroll={handleScroll}
              onWheel={handleWheel}
              onClick={handleMessageClick}
              style={isExpanded && expandedInputHeight > 0 ? { paddingBottom: `${expandedInputHeight}px` } : undefined}
            >
              {renderChatContent()}
            </MessagesList>
            <MessagesScrollGradient $position="bottom" $visible={!isExpanded && showMessagesBottomGradient} />
          </MessagesListWrapper>
        )}
        <ExpandedContentCenter
          $isExpanded={isExpanded}
          $emptyExpanded={emptyExpanded}
          $showTopFade={showMessagesBottomGradient}
          ref={isExpanded ? expandedInputRef : undefined}
        >
          {!emptyExpanded && suggestedActionsNode}
          {inputSection}
        </ExpandedContentCenter>
      </ChatContainer>
    </SidebarInner>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const updateConversationModal = (
    <ActionModal<UpdateConversationNameViewProps>
      ref={updateConversationModalRef}
      width="25rem"
      title={t('aiInsights.editConversationNameTitle')}
      submitButtonTitle={t('common.saveChanges')}
      Component={UpdateConversationNameView}
      componentProps={{
        conversationId: conversationId ?? '',
        name: currentConversationTitle,
      }}
    />
  );

  if (isInMobile) {
    return (
      <>
        <StyledBottomSheet open={isOpen} onDismiss={closeSidebar} scrollLocking={false}>
          <MobileBottomSheetContent>{renderInner()}</MobileBottomSheetContent>
        </StyledBottomSheet>
        {updateConversationModal}
        {infoModalNode}
      </>
    );
  }

  return (
    <>
      <MotionSidebarWrapper
        $isOpen={isOpen}
        style={{ width: sidebarWidthMV as unknown as number }}
        onWheel={(e) => e.stopPropagation()}
      >
        {!isExpanded && <ResizeHandle onMouseDown={handleResizeMouseDown} />}
        {renderInner(true)}
      </MotionSidebarWrapper>
      {updateConversationModal}
      {infoModalNode}
    </>
  );
};

export default AIInsightsSidebar;
